// src/admin/components/sidebar/Sidebar.jsx - Apartment Management System
import { useState, useEffect } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import {
  RiDashboardLine, RiShieldUserLine, RiUserLine, RiHomeHeartLine,
  RiTeamLine, RiMoneyDollarCircleLine, RiFileChartLine, RiSettings4Line,
  RiLogoutBoxLine, RiBuildingLine, RiArrowDownSLine, RiArrowRightSLine,
  RiArrowLeftSLine, RiMenu4Line, RiAddLine, RiFileList3Line, RiUserStarLine,
  RiArchiveLine, RiCalendarEventLine, RiUserHeartLine, RiFileUserLine,
  RiPieChartLine, RiBarChartBoxLine, RiLineChartLine, RiUserSettingsLine,
  RiAdminLine, RiCommunityLine, RiWallet3Line, RiFileTextLine,
  RiHistoryLine, RiReceiptLine, RiSecurePaymentLine, RiFileHistoryLine, RiServiceLine, RiWifiLine,
  RiWhatsappLine, RiCalendarCheckLine, RiCalendarLine, RiOrganizationChart, RiHandCoinLine
} from 'react-icons/ri';
import api from '../../../services/api';
import { useTheme } from '../../../contexts/ThemeContext';
import { useAuth } from '../../../contexts/AuthContext';
import { getAllBuildings } from '../../../models/buildingModel';

const Sidebar = ({ isOpen, setIsOpen, isMobile }) => {
  const { t } = useTranslation();
  const { isDarkMode, isRTL } = useTheme();
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [expandedMenus, setExpandedMenus] = useState({});
  const [firstBuildingId, setFirstBuildingId] = useState(null);
  const [orgName, setOrgName] = useState('');
  const isAdmin = user?.role === 'admin';
  const isFrontDesk = user?.role === 'front_desk';
  const isAccountant = user?.role === 'accountant';
  const isSales = user?.role === 'sales';
  const isManagement = isAdmin || isFrontDesk;
  const canAddSales = isManagement || isSales;
  const canAddPayments = isManagement || isAccountant;
  const canViewReports = isAdmin || isAccountant;

  // Initialize expanded state based on current location
  useEffect(() => {
    const path = location.pathname;
    const menuState = {};

    menuItems.forEach(section => {
      section.items.forEach(item => {
        if (item.subItems && path.startsWith(item.path)) {
          menuState[item.path] = true;
        }
      });
    });

    setExpandedMenus(menuState);
  }, [location.pathname]);

  // Fetch first available building
  useEffect(() => {
    const fetchFirstBuilding = async () => {
      try {
        const buildingsData = await getAllBuildings();
        console.log('Sidebar buildings fetch result:', buildingsData);

        // Handle various response formats: direct array, { buildings: [...] }, or { data: [...] }
        let buildings = [];
        if (Array.isArray(buildingsData)) {
          buildings = buildingsData;
        } else if (buildingsData?.buildings && Array.isArray(buildingsData.buildings)) {
          buildings = buildingsData.buildings;
        } else if (buildingsData?.data && Array.isArray(buildingsData.data)) {
          buildings = buildingsData.data;
        }

        if (buildings && buildings.length > 0) {
          const id = buildings[0].id || buildings[0]._id;
          console.log('Setting firstBuildingId to:', id);
          setFirstBuildingId(id);
        } else {
          console.log('No buildings found for sidebar link');
        }
      } catch (error) {
        console.error('Error fetching first building for sidebar:', error);
      }
    };

    fetchFirstBuilding();
  }, [user]);

  // Fetch organization name
  useEffect(() => {
    const fetchOrgSettings = async () => {
      try {
        const response = await api.get('/organization/settings');
        if (response.data.success && response.data.organization) {
          setOrgName(response.data.organization.name);
        }
      } catch (error) {
        console.error('Error fetching organization name for sidebar:', error);
      }
    };

    fetchOrgSettings();
  }, []);

  // Check if a route is active, including nested routes
  const isRouteActive = (path) => {
    return location.pathname.startsWith(path);
  };

  // Toggle submenu
  const toggleSubmenu = (path) => {
    setExpandedMenus(prev => ({
      ...prev,
      [path]: !prev[path]
    }));
  };

  // Handle logout
  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Menu items with organized sections
  const menuItems = [
    // Core Section
    {
      section: 'core',
      items: [
        {
          path: '/admin/dashboard',
          name: t('dashboard'),
          icon: <RiDashboardLine size={20} />,
          exact: true
        }
      ]
    },

    // User Management Section (Admin Only)
    ...(isAdmin ? [{
      section: 'user_management',
      items: [
        {
          path: '/admin/users',
          name: t('system_users'),
          icon: <RiShieldUserLine size={20} />,
          subItems: [
            {
              path: '/admin/users',
              name: t('user_list'),
              icon: <RiUserStarLine size={18} />
            },
            {
              path: '/admin/users/add',
              name: t('add_user'),
              icon: <RiAddLine size={18} />
            }
          ]
        }
      ]
    }] : []),

    // Property Management Section
    {
      section: 'property_management',
      items: [
        {
          path: '/admin/buildings',
          name: t('buildings'),
          icon: <RiBuildingLine size={20} />,
          subItems: [
            {
              path: '/admin/buildings',
              name: t('building_list'),
              icon: <RiBuildingLine size={18} />
            },
            ...(isManagement ? [{
              path: '/admin/buildings/add',
              name: t('add_building'),
              icon: <RiAddLine size={18} />
            }] : []),
            ...(firstBuildingId ? [{
              path: `/admin/buildings/${firstBuildingId}`,
              name: t('building_view') || 'Building View',
              icon: <RiFileList3Line size={18} />
            }] : [])
          ]
        },
        {
          path: '/admin/apartments',
          name: t('apartments'),
          icon: <RiHomeHeartLine size={20} />,
          subItems: [
            {
              path: '/admin/apartments',
              name: t('apartment_list'),
              icon: <RiHomeHeartLine size={18} />
            },
            ...(isManagement ? [{
              path: '/admin/apartments/add',
              name: t('add_apartment'),
              icon: <RiAddLine size={18} />
            }] : [])
          ]
        },
        // {
        //   path: '/admin/bookings',
        //   name: t('bookings'),
        //   icon: <RiCalendarCheckLine size={20} />,
        //   subItems: [
        //     { name: t('booking_list'), path: '/admin/bookings', icon: <RiFileList3Line size={18} /> },
        //     { name: t('new_booking'), path: '/admin/bookings/add', icon: <RiAddLine size={18} /> },
        //     { name: t('booking_calendar'), path: '/admin/bookings/calendar', icon: <RiCalendarEventLine size={18} /> },
        //     { name: t('waitlist'), path: '/admin/bookings/waitlist', icon: <RiTeamLine size={18} /> },
        //     { name: t('analytics'), path: '/admin/bookings/analytics', icon: <RiPieChartLine size={18} /> }
        //   ]
        // },
        {
          path: '/admin/owners',
          name: t('owners'),
          icon: <RiUserLine size={20} />,
          subItems: [
            { name: t('owner_list'), path: '/admin/owners', icon: <RiFileUserLine size={18} /> },
            ...(isManagement || isSales ? [{
              path: '/admin/owners/add',
              name: t('add_owner'),
              icon: <RiUserHeartLine size={18} />
            }] : [])
          ]
        },
        {
          path: '/admin/sales',
          name: t('sales'),
          icon: <RiHandCoinLine size={20} />,
          subItems: [
            { name: t('sale_list'), path: '/admin/sales', icon: <RiFileList3Line size={18} /> },
            ...(canAddSales ? [{
              path: '/admin/sales/add',
              name: t('new_sale'),
              icon: <RiAddLine size={18} />
            }] : [])
          ]
        },
        {
          path: '/admin/tenants',
          name: t('tenants'),
          icon: <RiTeamLine size={20} />,
          subItems: [
            { name: t('tenant_list'), path: '/admin/tenants', icon: <RiCommunityLine size={18} /> },
            ...(isManagement || isSales ? [{
              path: '/admin/tenants/add',
              name: t('add_tenant'),
              icon: <RiAddLine size={18} />
            }] : []),
            { name: t('overdue_rent'), path: '/admin/tenants/overdue', icon: <RiHistoryLine size={18} /> }
          ]
        }
      ]
    },

    // Financial Management Section
    {
      section: 'financial_management',
      items: [
        {
          path: '/admin/payments',
          name: t('payments'),
          icon: <RiMoneyDollarCircleLine size={20} />,
          subItems: [
            { name: t('all_payments'), path: '/admin/payments', icon: <RiWallet3Line size={18} /> },
            ...(canAddPayments ? [{
              path: '/admin/payments/add',
              name: t('record_payment'),
              icon: <RiSecurePaymentLine size={18} />
            }] : [])
          ]
        },
        {
          path: '/admin/services',
          name: t('service_payments'),
          icon: <RiWifiLine size={20} />,
          subItems: [
            { name: t('all_service_payments'), path: '/admin/services/all', icon: <RiWallet3Line size={18} /> },
            { name: t('manage_services'), path: '/admin/services', icon: <RiServiceLine size={18} /> },
            { name: t('generate_payments'), path: '/admin/services/generate', icon: <RiAddLine size={18} /> },
            { name: t('service_statistics'), path: '/admin/services/stats', icon: <RiPieChartLine size={18} /> }
          ]
        },
      ]
    },

    // Reports Section (Admin + Accountant)
    ...(canViewReports ? [{
      section: 'reports_analytics',
      items: [
        {
          path: '/admin/reports',
          name: t('reports'),
          icon: <RiFileChartLine size={20} />,
          subItems: [
            { name: t('reports_dashboard'), path: '/admin/reports', icon: <RiBarChartBoxLine size={18} /> },
            { name: t('revenue_report'), path: '/admin/reports/revenue', icon: <RiMoneyDollarCircleLine size={18} /> },
            { name: t('payment_logs'), path: '/admin/reports/payment-logs', icon: <RiHistoryLine size={18} /> },
            { name: t('occupancy_report'), path: '/admin/reports/occupancy', icon: <RiBuildingLine size={18} /> },
            { name: t('monthly_payments_report'), path: '/admin/reports/monthly-payments', icon: <RiCalendarLine size={18} /> }
          ]
        }
      ]
    }] : []),
    // Logs Section (Admin + Accountant)
    ...(canViewReports ? [{
      section: 'logs_audit',
      items: [
        {
          path: '/admin/logs',
          name: t('activity_logs'),
          icon: <RiFileHistoryLine size={20} />,
          subItems: [
            { name: t('all_activities'), path: '/admin/logs', icon: <RiFileHistoryLine size={18} /> },
            { name: t('tenant_activities'), path: '/admin/logs/tenants', icon: <RiTeamLine size={18} /> },
            { name: t('monthly_revenue'), path: '/admin/logs/monthly-revenue', icon: <RiBarChartBoxLine size={18} /> }
          ]
        }
      ]
    }] : []),

    // System Section
    {
      section: 'system',
      items: [
        {
          path: '/admin/settings',
          name: t('settings'),
          icon: <RiSettings4Line size={20} />,
          subItems: [
            { name: t('profile_settings'), path: '/admin/settings/profile', icon: <RiUserSettingsLine size={18} /> },
            // Subscription link — hidden for custom1 (SUBSCRIPTION_BYPASS)
            ...(isAdmin ? [{
              path: '/admin/settings/system',
              name: t('system_settings'),
              icon: <RiAdminLine size={18} />
            }] : []),
            ...(isManagement ? [{
              path: '/admin/settings/whatsapp',
              name: t('whatsapp'),
              icon: <RiWhatsappLine size={18} />
            }] : [])
          ]
        }
      ]
    }
  ];

  // RTL-aware arrow components
  const ArrowDown = () => <RiArrowDownSLine size={18} />;
  const ArrowRight = () => {
    return isRTL ? <RiArrowLeftSLine size={18} /> : <RiArrowRightSLine size={18} />;
  };

  // Toggle sidebar
  const toggleSidebar = () => {
    setIsOpen(!isOpen);
  };

  // Animation variants
  const sidebarVariants = {
    open: { width: 280, x: 0 },
    closed: { width: isMobile ? 280 : 80, x: isMobile ? (isRTL ? '100%' : '-100%') : 0 },
  };

  const textVariants = {
    open: { opacity: 1, x: 0 },
    closed: { opacity: 0, x: -10 },
  };

  const rtlTextVariants = {
    open: { opacity: 1, x: 0 },
    closed: { opacity: 0, x: 10 },
  };

  const submenuVariants = {
    open: { height: 'auto', opacity: 1 },
    closed: { height: 0, opacity: 0 },
  };

  const submenuItemVariants = {
    open: { x: 0, opacity: 1 },
    closed: { x: isRTL ? 10 : -10, opacity: 0 },
  };

  return (
    <motion.aside
      variants={sidebarVariants}
      animate={isOpen ? 'open' : 'closed'}
      transition={{ duration: 0.3, ease: 'easeInOut' }}
      className={`h-screen ${isDarkMode ? 'bg-zinc-950 border-zinc-800' : 'bg-white border-gray-200'
        } ${isRTL ? 'border-l' : 'border-r'} flex flex-col shadow-xl fixed z-50`}
      style={{
        position: 'fixed',
        top: 0,
        bottom: 0,
        [isRTL ? 'right' : 'left']: 0,
        overflowX: 'hidden',
        direction: isRTL ? 'rtl' : 'ltr'
      }}
    >
      {/* Sidebar Header */}
      <div className={`flex items-center justify-between h-16 ${isDarkMode ? 'border-zinc-800' : 'border-gray-200'} border-b px-4`}>
        <div className={`flex items-center ${isOpen ? 'gap-3 overflow-hidden' : 'justify-center w-full'}`}>
          <div className="flex items-center justify-center flex-shrink-0">
            {isOpen ? (
              <img
                src={isDarkMode ? "/quadcores-dark.png" : "/quadcores-light.png"}
                alt="QuadCores Logo"
                className="h-8 w-auto" // Adjust height as needed
              />
            ) : (
              <img
                src={isDarkMode ? "/quadcores-dark.png" : "/quadcores-light.png"}
                alt="Logo"
                className="h-8 w-8 object-contain" // Icon-like representation when collapsed
              />
            )}
          </div>
          <AnimatePresence>
            {isOpen && orgName && (
              <motion.span
                variants={isRTL ? rtlTextVariants : textVariants}
                initial="closed"
                animate="open"
                exit="closed"
                className={`text-lg font-bold truncate ${isDarkMode ? 'text-white' : 'text-zinc-900'}`}
              >
                {orgName}
              </motion.span>
            )}
          </AnimatePresence>
        </div>

        {/* Toggle Button */}
        <button
          onClick={toggleSidebar}
          className={`p-1.5 rounded-full ${isDarkMode ? 'hover:bg-zinc-800 text-gray-300' : 'hover:bg-gray-100 text-gray-600'} ${!isOpen ? 'hidden' : ''}`}
          aria-label={isOpen ? 'Collapse sidebar' : 'Expand sidebar'}
        >
          {isRTL ? (
            <RiArrowRightSLine size={20} />
          ) : (
            <RiArrowLeftSLine size={20} />
          )}
        </button>
      </div>

      {/* Mobile Toggle Button - Visible when sidebar is collapsed */}
      {!isOpen && (
        <button
          onClick={toggleSidebar}
          className={`p-2 mx-auto mt-2 rounded-full ${isDarkMode ? 'bg-zinc-800 text-gray-300 hover:bg-zinc-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
          aria-label="Expand sidebar"
        >
          <RiMenu4Line size={20} />
        </button>
      )}

      {/* User Profile */}
      <div className={`p-4 ${isDarkMode ? 'border-zinc-800' : 'border-gray-200'} border-b`}>
        <div className={`flex items-center ${isOpen ? '' : 'justify-center'}`}>
          <div className="h-10 w-10 rounded-full bg-gradient-to-br from-red-500 to-red-600 flex items-center justify-center shadow-md">
            <span className="font-bold text-white text-sm">
              {user?.firstName?.charAt(0) || 'U'}
            </span>
          </div>
          <AnimatePresence>
            {isOpen && (
              <motion.div
                variants={isRTL ? rtlTextVariants : textVariants}
                initial="closed"
                animate="open"
                exit="closed"
                className="ms-3"
              >
                <p className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  {user?.firstName} {user?.lastName}
                </p>
                <p className={`text-xs ${isDarkMode ? 'text-zinc-400' : 'text-gray-500'}`}>
                  {user?.role === 'admin' ? t('administrator') :
                   user?.role === 'front_desk' ? t('front_desk') :
                   user?.role === 'accountant' ? t('accountant') :
                   user?.role === 'sales' ? t('sales_role') : t('front_desk')}
                </p>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Navigation Menu */}
      <nav className="flex-grow overflow-y-auto overflow-x-hidden scrollbar-thin scrollbar-thumb-zinc-600 scrollbar-track-transparent">
        {menuItems.map((section, idx) => (
          <div key={`section-${idx}`} className="mt-4 px-4">
            {/* Section Title */}
            <AnimatePresence>
              {isOpen && (
                <motion.div
                  variants={isRTL ? rtlTextVariants : textVariants}
                  initial="closed"
                  animate="open"
                  exit="closed"
                  className={`mb-2 text-xs font-semibold ${isDarkMode ? 'text-zinc-400' : 'text-gray-500'} uppercase tracking-wider text-start`}
                >
                  {t(section.section)}
                </motion.div>
              )}
            </AnimatePresence>

            {/* Menu Items */}
            <ul className="space-y-1">
              {section.items.map((item) => (
                <li key={item.path}>
                  {item.subItems ? (
                    <div>
                      {/* Parent Menu Item with Submenu */}
                      <button
                        onClick={() => toggleSubmenu(item.path)}
                        className={`flex items-center justify-between w-full py-2.5 px-3 rounded-lg transition-all ${isRouteActive(item.path)
                          ? isDarkMode
                            ? 'bg-zinc-800 text-white font-medium'
                            : 'bg-zinc-50 text-red-600 font-medium'
                          : isDarkMode
                            ? 'text-zinc-400 hover:text-white hover:bg-zinc-800'
                            : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                          }`}
                      >
                        <div className="flex items-center">
                          <span className={`flex items-center justify-center w-6 h-6 ${isRouteActive(item.path) ? isDarkMode ? 'text-red-400' : 'text-red-600' : ''}`}>
                            {item.icon}
                          </span>
                          <AnimatePresence>
                            {isOpen && (
                              <motion.span
                                variants={isRTL ? rtlTextVariants : textVariants}
                                initial="closed"
                                animate="open"
                                exit="closed"
                                className="ms-3 text-sm"
                              >
                                {item.name}
                              </motion.span>
                            )}
                          </AnimatePresence>
                        </div>
                        <AnimatePresence>
                          {isOpen && (
                            <motion.span
                              variants={textVariants}
                              initial="closed"
                              animate="open"
                              exit="closed"
                              className={`transition-transform duration-300 ${expandedMenus[item.path] ? 'rotate-180' : ''}`}
                            >
                              {expandedMenus[item.path] ? (
                                <ArrowDown />
                              ) : (
                                <ArrowRight />
                              )}
                            </motion.span>
                          )}
                        </AnimatePresence>
                      </button>

                      {/* Submenu Items */}
                      <AnimatePresence>
                        {isOpen && expandedMenus[item.path] && (
                          <motion.ul
                            variants={submenuVariants}
                            initial="closed"
                            animate="open"
                            exit="closed"
                            transition={{ duration: 0.2 }}
                            className="ms-8 mt-1 space-y-1 overflow-hidden"
                          >
                            {item.subItems.map((subItem, subIdx) => (
                              <motion.li
                                key={subItem.path}
                                variants={submenuItemVariants}
                                initial="closed"
                                animate="open"
                                exit="closed"
                                transition={{ delay: 0.05 * subIdx }}
                                className={isRTL ? "origin-right" : "origin-left"}
                              >
                                <NavLink
                                  to={subItem.path}
                                  className={({ isActive }) => `flex items-center py-2 px-3 rounded-md text-sm transition-colors ${isActive
                                    ? isDarkMode
                                      ? 'bg-red-900/20 text-red-400 font-medium'
                                      : 'bg-zinc-50 text-red-600 font-medium'
                                    : isDarkMode
                                      ? 'text-zinc-400 hover:text-white hover:bg-zinc-800/50'
                                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                                    }`}
                                >
                                  <span className="flex items-center justify-center w-5 h-5">
                                    {subItem.icon}
                                  </span>
                                  <span className="ms-2 text-sm text-start">
                                    {subItem.name}
                                  </span>
                                </NavLink>
                              </motion.li>
                            ))}
                          </motion.ul>
                        )}
                      </AnimatePresence>
                    </div>
                  ) : (
                    /* Single Menu Item (no submenu) */
                    <NavLink
                      to={item.path}
                      end={item.exact}
                      className={({ isActive }) => `flex items-center py-2.5 px-3 rounded-lg transition-colors ${isActive
                        ? isDarkMode
                          ? 'bg-red-600 text-white font-medium shadow-md shadow-red-900/20'
                          : 'bg-red-600 text-white font-medium shadow-md shadow-red-500/20'
                        : isDarkMode
                          ? 'text-zinc-400 hover:text-white hover:bg-zinc-800/70'
                          : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                        }`}
                    >
                      <span className="flex items-center justify-center w-6 h-6">
                        {item.icon}
                      </span>
                      <AnimatePresence>
                        {isOpen && (
                          <motion.span
                            variants={isRTL ? rtlTextVariants : textVariants}
                            initial="closed"
                            animate="open"
                            exit="closed"
                            className="ms-3 text-sm text-start"
                          >
                            {item.name}
                          </motion.span>
                        )}
                      </AnimatePresence>
                    </NavLink>
                  )}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </nav>

      {/* Logout Button */}
      <div className={`p-4 ${isDarkMode ? 'border-zinc-800' : 'border-gray-200'} border-t mt-auto`}>
        <button
          onClick={handleLogout}
          className={`flex items-center w-full py-2.5 px-3 rounded-lg transition-colors ${isDarkMode
            ? 'text-zinc-400 hover:text-white hover:bg-red-900/20'
            : 'text-gray-600 hover:text-red-600 hover:bg-zinc-50'
            }`}
        >
          <span className="flex items-center justify-center w-6 h-6">
            <RiLogoutBoxLine size={20} />
          </span>
          <AnimatePresence>
            {isOpen && (
              <motion.span
                variants={isRTL ? rtlTextVariants : textVariants}
                initial="closed"
                animate="open"
                exit="closed"
                className="ms-3 text-sm text-start"
              >
                {t('logout')}
              </motion.span>
            )}
          </AnimatePresence>
        </button>
      </div>
    </motion.aside>
  );
};

export default Sidebar;
